/*
  Composant <select> custom, partagé par la buy box (main-product-engagement-advanced) et par
  la barre d'achat collante (product-sticky-atc-advanced).

  Principe : le <select> natif reste dans le DOM et reste la SEULE source de vérité. Le
  composant ne fait que le refléter (rendu HTML, donc prix barrés et libellés sur plusieurs
  lignes possibles) et écrire dans `select.value` avant de déclencher un `change`. Sans
  JavaScript, ou si le montage échoue, c'est le <select> natif qui s'affiche : aucun état
  n'est perdu.

  Chaque appelant fournit son propre rendu (`renderContent`) : la buy box empile titre de
  variante + nom du plan, la barre collante aligne nom + prix. Le reste — ouverture, clavier,
  ARIA, fermeture au clic extérieur, synchronisation avec le natif — est commun.
*/
(function () {
  'use strict';

  if (window.CustomSelect) return;

  var CHEVRON_SVG =
    '<svg viewBox="0 0 12 8" width="12" height="8" focusable="false" aria-hidden="true">' +
    '<path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.6" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var uid = 0;

  /*
    Rendu par défaut : deux segments lus sur les data-attributs de l'option native
    (`data-title` / `data-plan`). À défaut, le texte brut de l'option.
  */
  function defaultRender(container, option) {
    container.textContent = '';
    if (!option) return;

    var hasSegments = 'title' in option.dataset || 'plan' in option.dataset;
    var segments = hasSegments
      ? [
          ['custom-select__option-title', option.dataset.title],
          ['custom-select__option-plan', option.dataset.plan]
        ]
      : [['custom-select__option-title', option.textContent.trim()]];

    segments.forEach(function (segment) {
      if (!segment[1]) return;
      var el = document.createElement('span');
      el.className = segment[0];
      el.textContent = segment[1];
      container.appendChild(el);
    });
  }

  function mount(root, select, options) {
    var opts = options || {};

    if (!root || !select || select.options.length === 0) return null;
    // Remontage (éditeur de thème, re-rendu de section) : on rend l'instance déjà en place
    // plutôt que d'empiler un second jeu d'écouteurs sur le même déclencheur.
    if (root._customSelect) return root._customSelect;

    var render = typeof opts.renderContent === 'function' ? opts.renderContent : defaultRender;
    var idPrefix = opts.idPrefix || 'CustomSelect-' + ++uid;

    // Le <select> natif doit vivre DANS la racine : c'est ce qui permet à la feuille de
    // style de le masquer une fois le composant monté, sans règle propre à l'appelant.
    if (select.parentElement !== root) root.appendChild(select);

    var trigger = root.querySelector('.custom-select__trigger');
    if (!trigger) {
      trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'custom-select__trigger';
      root.insertBefore(trigger, select.nextSibling);
    }

    var valueEl = trigger.querySelector('.custom-select__value');
    if (!valueEl) {
      valueEl = document.createElement('span');
      valueEl.className = 'custom-select__value';
      trigger.appendChild(valueEl);
    }

    if (!trigger.querySelector('.custom-select__chevron')) {
      var chevron = document.createElement('span');
      chevron.className = 'custom-select__chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML = CHEVRON_SVG;
      trigger.appendChild(chevron);
    }

    var list = root.querySelector('.custom-select__list');
    if (!list) {
      list = document.createElement('ul');
      list.className = 'custom-select__list';
      root.appendChild(list);
    }

    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    if (opts.ariaLabel) trigger.setAttribute('aria-label', opts.ariaLabel);
    trigger.id = idPrefix + '-trigger';

    list.id = idPrefix + '-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('tabindex', '-1');
    list.hidden = true;
    list.innerHTML = '';
    trigger.setAttribute('aria-controls', list.id);

    var items = [];
    for (var i = 0; i < select.options.length; i++) {
      var li = document.createElement('li');
      li.className = 'custom-select__option';
      li.id = idPrefix + '-option-' + i;
      li.setAttribute('role', 'option');
      li.dataset.value = select.options[i].value;
      list.appendChild(li);
      items.push(li);
    }

    var activeIndex = -1;

    function isOpen() {
      return root.classList.contains('is-open');
    }

    function selectable(index) {
      var opt = select.options[index];
      return !!opt && !opt.hidden && !opt.disabled;
    }

    function selectableCount() {
      var count = 0;
      for (var i = 0; i < select.options.length; i++) {
        if (selectable(i)) count += 1;
      }
      return count;
    }

    function isStatic() {
      return root.classList.contains('custom-select--static');
    }

    /* Rendu complet depuis le <select> natif : libellés, option courante, options masquées.
       Les appelants n'écrivent jamais dans le composant, ils modifient le natif puis
       appellent refresh(). Une seule direction de flux, donc aucun état à resynchroniser. */
    function refresh() {
      var selected = select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
      render(valueEl, selected);

      items.forEach(function (item, index) {
        var opt = select.options[index];
        if (!opt) return;
        render(item, opt);
        item.hidden = !!opt.hidden;
        var isSelected = index === select.selectedIndex;
        item.classList.toggle('is-selected', isSelected);
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      /* Un choix unique n'est pas un choix. Le composant se fige alors : le declencheur
         garde son cadre et sa valeur — c'est la l'information — mais cesse d'etre un
         controle (plus de chevron, plus d'ouverture, hors du parcours clavier). L'etat est
         recalcule a chaque refresh() et non une fois au montage : les appelants masquent des
         options a la volee (combinaisons de variantes infaisables), le nombre de valeurs
         reellement proposees change donc en cours de vie. */
      var frozen = selectableCount() <= 1;
      root.classList.toggle('custom-select--static', frozen);
      trigger.disabled = frozen;
      if (frozen) {
        close();
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-controls');
      } else {
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-controls', list.id);
      }
    }

    function setActive(index) {
      // On ne s'arrête jamais sur une option masquée : la navigation clavier doit voir
      // exactement ce que la souris voit.
      var count = items.length;
      if (count === 0) return;

      var step = index >= activeIndex ? 1 : -1;
      var start = Math.max(0, Math.min(index, count - 1));
      var target = -1;
      var i;

      for (i = start; i >= 0 && i < count; i += step) {
        if (selectable(i)) { target = i; break; }
      }
      // Rien de sélectionnable dans le sens du déplacement : on repart en sens inverse
      // plutôt que de laisser le focus nulle part.
      if (target === -1) {
        for (i = start; i >= 0 && i < count; i -= step) {
          if (selectable(i)) { target = i; break; }
        }
      }
      if (target === -1) return;

      activeIndex = target;
      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === activeIndex);
      });
      trigger.setAttribute('aria-activedescendant', items[activeIndex].id);
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    function onDocumentPointerDown(event) {
      if (!root.contains(event.target)) close();
    }

    function open() {
      if (isOpen() || isStatic()) return;
      list.hidden = false;
      root.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      setActive(Math.max(0, select.selectedIndex));
      document.addEventListener('pointerdown', onDocumentPointerDown);
    }

    function close() {
      if (!isOpen()) return;
      list.hidden = true;
      root.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      items.forEach(function (item) {
        item.classList.remove('is-active');
      });
      activeIndex = -1;
      document.removeEventListener('pointerdown', onDocumentPointerDown);
    }

    function commit(value) {
      close();
      trigger.focus();
      if (select.value === value) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      // `change` est synchrone : à ce point l'appelant a fini de réagir, y compris s'il a
      // ramené la sélection sur une autre valeur. On redessine donc à partir de l'état
      // FINAL du natif, et le déclencheur ne peut plus rester sur l'ancienne valeur même
      // si l'appelant oublie d'appeler refresh().
      refresh();
    }

    trigger.addEventListener('click', function () {
      if (isOpen()) close();
      else open();
    });

    trigger.addEventListener('keydown', function (event) {
      var opened = isOpen();
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!opened) open();
          else setActive(activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!opened) open();
          else setActive(activeIndex - 1);
          break;
        case 'Home':
          if (!opened) return;
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          if (!opened) return;
          event.preventDefault();
          setActive(items.length - 1);
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          if (!opened) open();
          else if (items[activeIndex]) commit(items[activeIndex].dataset.value);
          break;
        case 'Escape':
        case 'Esc':
          if (!opened) return;
          event.preventDefault();
          close();
          break;
        case 'Tab':
          close();
          break;
      }
    });

    list.addEventListener('click', function (event) {
      var item = event.target.closest('.custom-select__option');
      if (item) commit(item.dataset.value);
    });

    list.addEventListener('pointermove', function (event) {
      var item = event.target.closest('.custom-select__option');
      if (item) setActive(items.indexOf(item));
    });

    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    root.dataset.customSelectReady = 'true';

    refresh();

    var api = {
      root: root,
      select: select,
      trigger: trigger,
      value: valueEl,
      list: list,
      options: items,
      refresh: refresh,
      open: open,
      close: close,
      isOpen: isOpen
    };

    root._customSelect = api;
    return api;
  }

  window.CustomSelect = { mount: mount, defaultRender: defaultRender };
})();
